<template>
  <div class="content-wrapper p-0">
    <div class="content">
      <h4 class="mb-3"><i class="fas fa-robot mr-2"></i>AI Assistant ของฉัน</h4>
      <div v-if="isLoading" class="text-center py-5"><b-spinner label="กำลังโหลด" /></div>
      <b-alert v-else-if="errorMessage" show variant="danger">
        {{ errorMessage }}
        <b-button size="sm" variant="outline-danger" class="ml-2" @click="load(true)">ลองใหม่</b-button>
      </b-alert>
      <template v-else-if="me">
        <b-alert v-if="!me.clinics.length" show variant="secondary">
          AI Assistant ใช้ได้เฉพาะผู้จัดการสาขาและผู้บริหารคลินิก
        </b-alert>

        <b-card v-for="clinic in me.clinics" :key="clinic.code" class="shadow-sm border-0 mb-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <h5 class="mb-0">{{ clinic.name }}</h5>
            <div>
              <b-badge variant="info" class="mr-1">
                {{ clinic.accessLevel === "clinic" ? "ผู้บริหาร (ทั้งคลินิก)" : "ผู้จัดการสาขา" }}
              </b-badge>
              <b-badge v-if="clinic.tier" variant="primary" class="mr-1">แพ็กเกจ {{ tierLabel(clinic.tier) }}</b-badge>
              <b-badge :variant="clinic.available ? 'success' : 'secondary'">
                {{ clinic.available ? "เปิดใช้งานแล้ว" : "คลินิกยังไม่เปิดใช้งาน" }}
              </b-badge>
            </div>
          </div>
          <p class="small text-muted mb-3">AI เห็นข้อมูลของ: {{ branchLabel(clinic) }}</p>
          <McpConnectGuide :connector-url="clinic.connectorUrl" :available="clinic.available" />
        </b-card>

        <b-card class="shadow-sm border-0">
          <h5 class="mb-3"><i class="fas fa-plug mr-2"></i>การเชื่อมต่อของฉัน</h5>
          <b-alert v-if="revokeError" show variant="danger">{{ revokeError }}</b-alert>
          <b-table :items="me.connections" :fields="fields" small responsive show-empty empty-text="ยังไม่มีการเชื่อมต่อ">
            <template #cell(clinic)="row">{{ row.item.clinic ? row.item.clinic.name : "-" }}</template>
            <template #cell(client)="row">
              <div>{{ row.item.clientName || "ไม่ระบุชื่อแอป" }}</div>
              <small class="text-muted">{{ row.item.redirectHost }}</small>
            </template>
            <template #cell(creationDt)="row">{{ formatDateTime(row.item.creationDt) }}</template>
            <template #cell(lastUsedAt)="row">{{ formatDateTime(row.item.lastUsedAt) }}</template>
            <template #cell(actions)="row">
              <b-button
                size="sm"
                variant="outline-danger"
                :disabled="revokingId === row.item.id"
                @click="confirmRevoke(row.item)"
              >
                ยกเลิกการเชื่อมต่อ
              </b-button>
            </template>
          </b-table>
        </b-card>
      </template>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import McpConnectGuide from "@/components/mcp/McpConnectGuide.vue";
import { mcpErrorMessage } from "@/components/mcp/mcpErrors";
import { mcpTierLabel } from "@/components/mcp/mcpLabels";

export default {
  name: "UserAiAssistant",
  components: { McpConnectGuide },
  data() {
    return {
      isLoading: false,
      errorMessage: "",
      revokeError: "",
      revokingId: null,
      fields: [
        { key: "clinic", label: "คลินิก" },
        { key: "client", label: "แอป AI" },
        { key: "creationDt", label: "เชื่อมต่อเมื่อ" },
        { key: "lastUsedAt", label: "ใช้งานล่าสุด" },
        { key: "actions", label: "" },
      ],
    };
  },
  computed: {
    me() {
      return this.$store.getters["moduleMcpSetting/me"];
    },
  },
  created() {
    this.load(true);
  },
  methods: {
    tierLabel: mcpTierLabel,
    formatDateTime(value) {
      return value ? moment(value).format("DD/MM/YYYY HH:mm") : "-";
    },
    branchLabel(clinic) {
      if (clinic.accessLevel === "clinic") return `ทุกสาขา (${clinic.branches.length} สาขา)`;
      return clinic.branches.map(branch => branch.name || `#${branch.id}`).join(", ");
    },
    async load(force) {
      this.isLoading = true;
      this.errorMessage = "";
      try {
        await this.$store.dispatch("moduleMcpSetting/fetchMe", { force });
      } catch (error) {
        this.errorMessage = mcpErrorMessage(error, "โหลดข้อมูล AI Assistant ไม่สำเร็จ");
      } finally {
        this.isLoading = false;
      }
    },
    async confirmRevoke(connection) {
      const clinicName = connection.clinic ? connection.clinic.name : "คลินิก";
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `ยกเลิกการเชื่อมต่อ ${connection.clientName || "แอป AI"} กับ ${clinicName}? ต้องเชื่อมต่อใหม่หากต้องการใช้อีกครั้ง`,
        {
          title: "ยืนยันการยกเลิกการเชื่อมต่อ",
          okVariant: "danger",
          okTitle: "ยกเลิกการเชื่อมต่อ",
          cancelTitle: "ไม่ใช่",
          centered: true,
        }
      );
      if (!confirmed) return;
      this.revokingId = connection.id;
      this.revokeError = "";
      try {
        await this.$store.dispatch("moduleMcpSetting/revokeMyConnection", { connectionId: connection.id });
      } catch (error) {
        this.revokeError = mcpErrorMessage(error, "ยกเลิกการเชื่อมต่อไม่สำเร็จ กรุณาลองใหม่");
      } finally {
        this.revokingId = null;
      }
    },
  },
};
</script>
